import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  NormalizedCacheObject,
  TypePolicies,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import fetch from 'cross-fetch';

export interface Config {
  url: string;
  getAccessToken?: () => Promise<string>;
  includeCredentials?: boolean;
  debug?: boolean;
  cachePolicies?: TypePolicies;
}

export type Api = ApolloClient<NormalizedCacheObject>;

let client: Api | undefined;
let getAccessToken: undefined | (() => Promise<string> | string);

export const setAppOriginContext = setContext(async (_, { headers }) => {
  return {
    headers: {
      ...headers,
      'X-App-Origin': 'decoupled',
    },
  };
});

export const authLink = setContext(async (_, { headers }) => {
  if (getAccessToken === undefined) {
    return headers;
  }

  const token = await getAccessToken();

  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  };
});

export const createApi = ({
  url,
  getAccessToken: newGetAccessToken,
  includeCredentials = false,
  debug = false,
  cachePolicies,
}: Config): Api => {
  getAccessToken = newGetAccessToken;

  if (client === undefined) {
    const httpLink = createHttpLink({
      fetch,
      uri: url,
      credentials: includeCredentials ? 'include' : undefined,
    });

    client = new ApolloClient({
      link: from([setAppOriginContext, authLink, httpLink]),
      cache: new InMemoryCache({ typePolicies: cachePolicies }),
      connectToDevTools: debug,
    });
  }

  return client;
};

export const setApiGetAccessToken = (
  newGetAccessToken: typeof getAccessToken
): void => {
  getAccessToken = newGetAccessToken;
};

export const getApi = (): Api => {
  if (client === undefined) {
    throw new Error('Api client not available');
  }

  return client;
};
